import { type FormEvent, useRef, useState } from 'react';

import { sendFeedbackApi } from '@/lib/api/feedbackApi';
import { useAuthStore } from '@/stores/auth-store';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { FeedbackSource, FeedbackType } from '@shared/enums/domain.enum';
import { AlertTriangle, Bug, CheckCircle2, Lightbulb, MessageCircle, Send, Star, X } from 'lucide-react';
import { toast } from 'sonner';

const SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined;

type TypeOption = {
  value: FeedbackType;
  label: string;
  icon: typeof Bug;
  activeClass: string;
};

const TYPE_OPTIONS: TypeOption[] = [
  { value: FeedbackType.BUG, label: 'Bug', icon: Bug, activeClass: 'border-red-500 bg-red-50 text-red-700' },
  { value: FeedbackType.AVIS, label: 'Avis', icon: Star, activeClass: 'border-amber-500 bg-amber-50 text-amber-700' },
  {
    value: FeedbackType.SUGGESTION,
    label: 'Suggestion',
    icon: Lightbulb,
    activeClass: 'border-blue-500 bg-blue-50 text-blue-700',
  },
];

export default function FeedbackWidget() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>(FeedbackType.BUG);
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const captchaRef = useRef<HCaptcha>(null);

  function resetForm() {
    setType(FeedbackType.BUG);
    setMessage('');
    setCaptchaToken('');
    setError(null);
    setIsSuccess(false);
    captchaRef.current?.resetCaptcha();
  }

  function close() {
    setOpen(false);
    setTimeout(resetForm, 200);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (message.trim().length < 5) {
      setError('Merci de détailler un peu votre retour (5 caractères minimum).');
      return;
    }
    if (SITE_KEY && !captchaToken) {
      setError('Merci de valider le captcha.');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendFeedbackApi({
        type,
        message: message.trim(),
        source: FeedbackSource.ADMIN,
        pageUrl: window.location.href,
        captchaToken,
      });
      setIsSuccess(true);
      toast.success('Merci pour votre retour !');
    } catch {
      setError("Impossible d'envoyer votre retour pour le moment. Réessayez plus tard.");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {/* Bouton flottant */}
      <button
        type='button'
        data-testid='feedback-fab'
        onClick={() => setOpen(o => !o)}
        aria-label='Donner mon avis'
        className='fixed bottom-16 right-3 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-300/50 transition-transform hover:-translate-y-0.5 hover:bg-red-700'
      >
        {open ? <X className='h-6 w-6' /> : <MessageCircle className='h-6 w-6' />}
      </button>

      {/* Panneau */}
      {open && (
        <div className='fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl border border-slate-200 bg-white shadow-2xl'>
          <div className='flex items-center justify-between rounded-t-2xl bg-red-600 px-5 py-4 text-white'>
            <div>
              <p className='text-sm font-bold'>Signaler / Suggérer</p>
              <p className='text-xs text-red-100'>Un retour sur le back-office ?</p>
            </div>
            <button type='button' onClick={close} aria-label='Fermer' className='rounded-full p-1 hover:bg-white/20'>
              <X className='h-5 w-5' />
            </button>
          </div>

          <div className='p-5'>
            {!isAuthenticated ? (
              <div className='rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600'>
                Vous devez être connecté pour nous partager votre avis.
              </div>
            ) : isSuccess ? (
              <div className='flex flex-col items-center gap-3 py-4 text-center'>
                <CheckCircle2 className='h-10 w-10 text-emerald-500' />
                <p className='font-bold text-slate-900'>Merci pour votre retour !</p>
                <p className='text-sm text-slate-500'>Nous l'avons bien reçu.</p>
                <button type='button' onClick={resetForm} className='mt-1 text-sm font-bold text-red-600 underline'>
                  Envoyer un autre retour
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className='space-y-4'>
                <div className='grid grid-cols-3 gap-2'>
                  {TYPE_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const active = type === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type='button'
                        onClick={() => setType(opt.value)}
                        className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs font-semibold transition ${active ? opt.activeClass : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      >
                        <Icon className='h-5 w-5' />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                <textarea
                  rows={4}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={2000}
                  placeholder='Décrivez votre retour...'
                  className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-red-500'
                  disabled={isSubmitting}
                />

                {SITE_KEY && (
                  <div className='flex justify-center'>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={SITE_KEY}
                      onVerify={token => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                    />
                  </div>
                )}

                {error && (
                  <p className='flex items-center gap-1.5 text-xs font-medium text-red-600'>
                    <AlertTriangle className='h-4 w-4 shrink-0' />
                    {error}
                  </p>
                )}

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  <Send className='h-4 w-4' />
                  {isSubmitting ? 'Envoi...' : 'Envoyer'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
